import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";
import { AnimatedBox } from "./components/AnimatedBox";
import { FlowArrow } from "./components/FlowArrow";
import { DataParticle } from "./components/DataParticle";

interface PipelineStep {
  label: string;
  sublabel: string;
  icon: string;
  x: number;
  color: string;
  delay: number;
}

// Pipeline centered horizontally with wider spacing
const STEPS: PipelineStep[] = [
  { label: "Fetch", sublabel: "API / Upload", icon: "📥", x: 240, color: COLORS.accentBlue, delay: 25 },
  { label: "Parse", sublabel: "Docling OCR", icon: "🔍", x: 540, color: COLORS.accentPurple, delay: 50 },
  { label: "Chunk", sublabel: "AST / Heading", icon: "✂️", x: 840, color: COLORS.accentTeal, delay: 75 },
  { label: "Embed", sublabel: "1536-dim", icon: "🧮", x: 1140, color: COLORS.accentPink, delay: 100 },
  { label: "Store", sublabel: "pgvector", icon: "💾", x: 1440, color: COLORS.accentGreen, delay: 125 },
];

const PIPELINE_Y = 480;

const SOURCES = [
  { name: "GitHub", icon: "⚙️", color: COLORS.github },
  { name: "Jira", icon: "📋", color: COLORS.jira },
  { name: "Confluence", icon: "📄", color: COLORS.confluence },
  { name: "Slack", icon: "💬", color: COLORS.slack },
  { name: "Upload", icon: "📎", color: COLORS.accentOrange },
];

export const Scene4Ingestion: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  // Exit
  const exitOpacity = interpolate(frame, [270, 300], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title — top area */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            fontSize: 44,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          Document Ingestion{" "}
          <span style={{ color: COLORS.accentTeal }}>Pipeline</span>
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 10,
          }}
        >
          5-step sync worker — serial processing for memory safety
        </div>
      </div>

      {/* Source types — horizontal row above pipeline */}
      <div
        style={{
          position: "absolute",
          top: 220,
          left: "50%",
          transform: "translate(-50%, 0)",
          display: "flex",
          gap: 32,
        }}
      >
        {SOURCES.map((src, i) => {
          const srcAppear = spring({
            frame: frame - 5 - i * 5,
            fps,
            config: SPRING.snappy,
          });
          return (
            <div
              key={i}
              style={{
                opacity: interpolate(srcAppear, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(srcAppear, [0, 1], [-20, 0])}px)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: COLORS.bgCard,
                  border: `1px solid ${COLORS.borderLight}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                }}
              >
                {src.icon}
              </div>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: COLORS.textMuted,
                }}
              >
                {src.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* Funnel arrows from sources down to Fetch box */}
      {(() => {
        const funnelAppear = spring({
          frame: frame - 20,
          fps,
          config: SPRING.gentle,
        });
        return (
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              opacity: interpolate(funnelAppear, [0, 1], [0, 0.4]),
            }}
          >
            {/* Converging dashed lines from source row center to Fetch box */}
            {SOURCES.map((_, i) => {
              const srcCenterX = 960 - (SOURCES.length - 1) * 44 + i * 88;
              return (
                <line
                  key={i}
                  x1={srcCenterX}
                  y1={305}
                  x2={STEPS[0].x}
                  y2={PIPELINE_Y - 50}
                  stroke={COLORS.accentBlue}
                  strokeWidth={1}
                  strokeDasharray="4 6"
                  strokeDashoffset={-frame * 1.5}
                  opacity={0.5}
                />
              );
            })}
          </svg>
        );
      })()}

      {/* Pipeline steps — centered band */}
      {STEPS.map((step, i) => (
        <React.Fragment key={i}>
          <AnimatedBox
            label={step.label}
            sublabel={step.sublabel}
            icon={step.icon}
            x={step.x}
            y={PIPELINE_Y}
            width={190}
            height={95}
            color={step.color}
            delay={step.delay}
            fontSize={18}
            borderRadius={16}
          />

          {/* Arrows between steps */}
          {i < STEPS.length - 1 && (
            <FlowArrow
              fromX={step.x + 100}
              fromY={PIPELINE_Y}
              toX={STEPS[i + 1].x - 100}
              toY={PIPELINE_Y}
              delay={step.delay + 15}
              color={step.color}
              strokeWidth={2}
            />
          )}

          {/* Data particles between steps */}
          {i < STEPS.length - 1 && frame > step.delay + 30 && (
            <DataParticle
              fromX={step.x + 105}
              fromY={PIPELINE_Y}
              toX={STEPS[i + 1].x - 105}
              toY={PIPELINE_Y}
              delay={step.delay + 30}
              duration={35}
              color={step.color}
              size={5}
              count={2}
            />
          )}
        </React.Fragment>
      ))}

      {/* Stats row — well below pipeline with breathing room */}
      <div
        style={{
          position: "absolute",
          top: 640,
          left: "50%",
          transform: "translate(-50%, 0)",
          display: "flex",
          gap: 100,
        }}
      >
        {[
          { label: "SHA-256 Dedup", value: "Skip unchanged", delay: 150 },
          { label: "Batch Size", value: "8 chunks/call", delay: 165 },
          { label: "Serial Processing", value: "DOC_CONCURRENCY=1", delay: 180 },
        ].map((stat, i) => {
          const statAppear = spring({
            frame: frame - stat.delay,
            fps,
            config: SPRING.snappy,
          });
          return (
            <div
              key={i}
              style={{
                opacity: interpolate(statAppear, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(statAppear, [0, 1], [15, 0])}px)`,
                textAlign: "center",
                minWidth: 180,
              }}
            >
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 22,
                  fontWeight: 700,
                  color: COLORS.accentTeal,
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  color: COLORS.textMuted,
                  marginTop: 6,
                }}
              >
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar — bottom with generous padding */}
      {(() => {
        const barAppear = spring({
          frame: frame - 145,
          fps,
          config: SPRING.gentle,
        });
        const barProgress = interpolate(frame, [150, 260], [0, 100], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        return (
          <div
            style={{
              position: "absolute",
              left: 260,
              bottom: 100,
              width: 1400,
              opacity: interpolate(barAppear, [0, 1], [0, 1]),
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  color: COLORS.textMuted,
                }}
              >
                Sync Progress
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  color: COLORS.accentTeal,
                  fontWeight: 600,
                }}
              >
                {Math.round(barProgress)}%
              </span>
            </div>
            <div
              style={{
                width: "100%",
                height: 8,
                borderRadius: 4,
                background: COLORS.bgCard,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <div
                style={{
                  width: `${barProgress}%`,
                  height: "100%",
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${COLORS.accentBlue}, ${COLORS.accentTeal})`,
                  boxShadow: `0 0 12px ${COLORS.accentTeal}40`,
                  transition: "width 0.1s",
                }}
              />
            </div>
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
